import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { Controller, useForm } from 'react-hook-form';
import { useRecoveryStore } from '@lib/store';
import { useRouteHook } from '@shared/hook';
import { RoutePaths } from '@shared/utils';
import { ButtonV2, Input, Select } from '@shared/widgets';

const generateYears = () => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const YEARS_MAX_OFFSET = 100;
  const YEARS_MIN_OFFSET = 18;
  const years = [];

  for (let i = YEARS_MIN_OFFSET; i <= YEARS_MAX_OFFSET; i++) {
    const calcYear = currentYear - i;
    years.push({
      value: calcYear.toString(),
      leftLabel: calcYear.toString(),
    });
  }

  return years;
};

const getDaysByMonth = (month: number, year: number) => {
  return new Date(year, month, 0).getDate();
};

const generateDays = (month: DateArrayType, year?: DateArrayType) => {
  const days = [];

  if (!month?.value) {
    for (let i = 1; i <= 31; i++) {
      days.push({
        value: ('0' + i).slice(-2),
        leftLabel: ('0' + i).slice(-2),
      });
    }
    return days;
  }

  const currentYear = new Date().getFullYear().toString();
  const yearValue = year?.value || currentYear;

  const daysInMonth = month?.value ? getDaysByMonth(month.value as unknown as number, yearValue as unknown as number) : 31;

  for (let i = 1; i <= daysInMonth; i++) {
    days.push({
      value: ('0' + i).slice(-2),
      leftLabel: ('0' + i).slice(-2),
    });
  }

  return days;
};

const MONTHS: DateArrayType[] = [
  { leftLabel: 'January', value: '01' },
  { leftLabel: 'February', value: '02' },
  { leftLabel: 'March', value: '03' },
  { leftLabel: 'April', value: '04' },
  { leftLabel: 'May', value: '05' },
  { leftLabel: 'June', value: '06' },
  { leftLabel: 'July', value: '07' },
  { leftLabel: 'August', value: '08' },
  { leftLabel: 'September', value: '09' },
  { leftLabel: 'October', value: '10' },
  { leftLabel: 'November', value: '11' },
  { leftLabel: 'December', value: '12' },
];

interface IForm {
  firstName: string;
  lastName: string;
  birthPlace: string;
  month?: DateArrayType;
  day?: DateArrayType;
  year?: DateArrayType;
}

interface DateArrayType {
  leftLabel: string;
  value: string;
}

export const PasswordResetForm = () => {
  const { navigate } = useRouteHook();

  const recoveryStore = useRecoveryStore();

  const [daysOptions, setDaysOptions] = useState<{ value: string; leftLabel: string }[]>([]);

  const formControl = useForm<IForm>({
    mode: 'onChange',
    defaultValues: {
      firstName: '',
      lastName: '',
      birthPlace: '',
      month: undefined,
      day: undefined,
      year: undefined,
    },
  });

  const firstName = formControl.watch('firstName');
  const lastName = formControl.watch('lastName');
  const birthPlace = formControl.watch('birthPlace');
  const month = formControl.watch('month');
  const year = formControl.watch('year');
  const day = formControl.watch('day');

  useEffect(() => {
    setDaysOptions(generateDays(month!, year));
  }, [month, year]);

  const onSubmit = async (data: IForm) => {
    recoveryStore.recovery({
      BirthDate: `${data.year?.value}-${data.month?.value}-${data.day?.value}`,
      BirthPlace: data.birthPlace,
      FirstName: data.firstName,
      LastName: data.lastName,
    });
    navigate(RoutePaths.login.onNavigate());
  };

  return (
    <div>
      <div className='mb-4.5 text-center text-sm text-[#fcfcfc] uppercase'>RECOVER PASSWORD</div>
      <div>
        <form className='flex flex-col' onSubmit={formControl.handleSubmit(onSubmit)}>
          <Controller
            name='firstName'
            control={formControl.control}
            rules={{ maxLength: 100 }}
            render={({ field }) => <Input {...field} className='animat-input mb-4' placeholder='FIRST NAME' required />}
          />
          <Controller
            name='lastName'
            control={formControl.control}
            rules={{ maxLength: 100 }}
            render={({ field }) => <Input {...field} className='animat-input' placeholder='LAST NAME' required />}
          />
          <div className='my-4 flex justify-between gap-2'>
            <Controller
              name='month'
              control={formControl.control}
              rules={{ maxLength: 100 }}
              render={({ field: { value, onChange } }) => (
                <Select
                  placeholder='MONTH'
                  items={MONTHS}
                  value={value?.value}
                  onChange={onChange}
                  selectWidth='w-[140%] z-10 left-[-20%] top-[-10%] '
                />
              )}
            />
            <Controller
              name='day'
              control={formControl.control}
              rules={{ maxLength: 100 }}
              render={({ field: { value, onChange } }) => (
                <Select
                  placeholder='DAY'
                  items={daysOptions}
                  value={value?.value}
                  onChange={onChange}
                  selectWidth='w-[140%] z-10 left-[-20%] top-[-10%]'
                />
              )}
            />
            <Controller
              name='year'
              control={formControl.control}
              rules={{ maxLength: 100 }}
              render={({ field: { value, onChange } }) => (
                <Select
                  placeholder='YEAR'
                  items={generateYears()}
                  value={value?.value}
                  onChange={onChange}
                  selectWidth='w-[140%] z-10 left-[-20%] top-[-10%]'
                />
              )}
            />
          </div>
          <Controller
            name='birthPlace'
            control={formControl.control}
            rules={{ maxLength: 100 }}
            render={({ field }) => <Input {...field} className='animat-input' placeholder='PLACE OF BIRTH' required />}
          />
          <ButtonV2
            className='animat-btn-in mt-9.5 !h-[56px]'
            fullWidth
            type='submit'
            disabled={!firstName || !lastName || !birthPlace || !day || !month || !year}
          >
            SEND REQUEST
          </ButtonV2>
        </form>
        <Link to={RoutePaths.recovery.onNavigate()}>
          <ButtonV2 className='mt-7.5 !h-[56px]' color='outlined' fullWidth>
            Cancel
          </ButtonV2>
        </Link>
      </div>
    </div>
  );
};
